/**
 * CalculateLoadingOverlay — animated loader (design phase).
 *
 * Non-dismissable modal shown while `POST /api/engagements/calculate` is
 * in flight. The spinner was replaced with an animated word (letters
 * reveal in sequence) plus a sweeping light bar. The progressive copy
 * still drives the message text at 5s / 15s, so the auditor learns the
 * server may be cold-starting; only the *presentation* changed.
 *
 * Behaviour preserved verbatim:
 *   • progressive phases via useProgressiveLoader (normal/warming/coldStart)
 *   • Cancel button → onCancel (caller aborts the request)
 *   • every dismiss path defeated (no X, escape/outside-click inert)
 *   • sr-only DialogTitle for the a11y contract
 *   • data-testid + data-phase preserved for the test-suite
 */

import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useProgressiveLoader } from "@/hooks/useProgressiveLoader";

interface CalculateLoadingOverlayProps {
  open: boolean;
  onCancel: () => void;
}

/** Splits a word into animated per-letter spans with a staggered reveal. */
function AnimatedWord({ text }: { text: string }) {
  const letters = Array.from(text);
  const total = letters.length || 1;
  return (
    <span className="calc-loader-word" aria-hidden="true">
      {letters.map((ch, i) => (
        <span
          key={`${ch}-${i}`}
          className="calc-loader-letter"
          style={{ animationDelay: `${(i / total) * 1.2}s` }}
        >
          {ch}
        </span>
      ))}
    </span>
  );
}

export function CalculateLoadingOverlay({
  open,
  onCancel,
}: CalculateLoadingOverlayProps) {
  const { t } = useTranslation();
  const phase = useProgressiveLoader(open);

  const message =
    phase === "coldStart"
      ? t("engagement.calculate.coldStart")
      : phase === "warming"
        ? t("engagement.calculate.warming")
        : t("engagement.calculate.calculating");

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent
        showCloseButton={false}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        data-testid="calculate-loading-overlay"
        className="max-w-md"
      >
        <DialogTitle className="sr-only">
          {t("engagement.calculate.loadingDialogTitle")}
        </DialogTitle>
        <div className="flex flex-col items-center gap-4 py-8 text-center">
          <div className="calc-loader">
            {/* The animated word is keyed by `message` so React remounts
                the letter spans when the phase changes — re-triggering the
                staggered reveal for the new text. It carries the testid +
                phase + the visible (screen-reader) text so the suite's
                assertions on text + data-phase keep passing. */}
            <span
              data-testid="calculate-loading-message"
              data-phase={phase}
            >
              <span className="sr-only">{message}</span>
              <AnimatedWord key={message} text={message} />
            </span>
            <span className="calc-loader-bar-track" aria-hidden="true">
              <span className="calc-loader-bar" />
            </span>
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            data-testid="calculate-cancel-button"
          >
            {t("engagement.calculate.cancel")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
